// cookieConsent — single source of truth for the cookie-consent state.
//
// Three categories matching the EU "necessary / statistics / marketing"
// pattern. "Necessary" is always-on (session, CSRF, language). The
// other two require explicit opt-in.
//
// Persistence: localStorage so it survives reload but is per-device.
// A consent record is "fresh" for 365 days; after that the banner
// re-prompts (helps stay compliant with periodic re-consent).

export type ConsentCategory = 'necessary' | 'statistics' | 'marketing';

export interface ConsentState {
  necessary: true;            // hard-coded; can't be revoked
  statistics: boolean;
  marketing: boolean;
  /** ISO timestamp when this consent was recorded. */
  recordedAt: string;
  /** Schema version — bump if we add categories. */
  version: 1;
}

const STORAGE_KEY = 'fw.consent.v1';
const FRESHNESS_MS = 365 * 24 * 60 * 60 * 1000; // 365 days

export function loadConsent(): ConsentState | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ConsentState;
    if (parsed.version !== 1) return null;
    const age = Date.now() - new Date(parsed.recordedAt).getTime();
    if (Number.isNaN(age) || age > FRESHNESS_MS) return null;
    return {
      necessary: true,
      statistics: !!parsed.statistics,
      marketing: !!parsed.marketing,
      recordedAt: parsed.recordedAt,
      version: 1,
    };
  } catch {
    return null;
  }
}

export function saveConsent(opts: { statistics: boolean; marketing: boolean }): ConsentState {
  const state: ConsentState = {
    necessary: true,
    statistics: opts.statistics,
    marketing: opts.marketing,
    recordedAt: new Date().toISOString(),
    version: 1,
  };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // ignore storage errors (Safari private mode etc.)
  }
  // Fire a window event so analytics-loaders can pick up the change
  // without polling.
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('fw-consent-changed', { detail: state }));
  }
  return state;
}

/** Convenience: accept everything. */
export function acceptAll(): ConsentState {
  return saveConsent({ statistics: true, marketing: true });
}

/** Convenience: reject everything but necessary. */
export function rejectAll(): ConsentState {
  return saveConsent({ statistics: false, marketing: false });
}

/** True when no fresh consent exists → banner should be shown. */
export function shouldShowBanner(): boolean {
  return loadConsent() === null;
}

export function hasConsent(category: ConsentCategory): boolean {
  if (category === 'necessary') return true;
  const state = loadConsent();
  if (!state) return false;
  return state[category] === true;
}
